 
import { useState, useContext,useEffect, useMemo ,useCallback ,useRef} from "react";
import SafeDatePicker from "../components/safeDatePicker";
import CompanyList from "../components/CompanyList";
import ToDoCount from "../components/ToDoCount";
import BarchartReturns from "../components/BarchartReturns";
import BarchartStatus from "../components/BarcharStatus";
import ConnString from "../../../ConnString.js";
import { tokens, ColorModeContext } from "../../../theme.js";
import {Stack , Box ,IconButton, Typography, useTheme } from "@mui/material";
import FullscreenIcon from '@mui/icons-material/Fullscreen';

const Connstring2 = ConnString.connstring;

const STATUS_FILTERS = {
  ALL: "ALL",
  DONE: "Done",
  PENDING: "Pending",
  DRAFTED: "Drafted",
  FOR_PAYMENT: "For Payment",
  TODO: "To Do",
};

const ClientMonitoring = () => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    useContext(ColorModeContext);

    const today = new Date();

// First day of current month
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

// Last day of current month
const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

const [selectedDateFrom, setSelectedDateFrom] = useState(firstDayOfMonth);
const [selectedDateTo, setSelectedDateTo] = useState(lastDayOfMonth);


  const [companies, setCompanies] = useState([]);
  // const [selectedDateFrom, setSelectedDateFrom] = useState(new Date());
  // const [selectedDateTo, setSelectedDateTo] = useState(new Date());

  // ✅ Multi-filter states
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.ALL);
  const [returnFilter, setReturnFilter] = useState("ALL"); // Returnx filter
  const [searchCompany, setSearchCompany] = useState("");  // CompanyName search

   const [isFullscreen, setIsFullscreen] = useState(false);
    const matrixRef = useRef(null);


  const compID = sessionStorage.getItem("compID");

  const formattedDateFrom = selectedDateFrom.toISOString().split("T")[0];
  const formattedDateTo = selectedDateTo.toISOString().split("T")[0];

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${Connstring2}/api/DashboardCMcompanylist`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyID: compID,
          datefrom: formattedDateFrom,
          dateto: formattedDateTo,
        }),
      });

      const data = await response.json();
      if (Array.isArray(data)) setCompanies(data);
      else console.error("Data format is incorrect. Expected an array.", data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  }, [compID, formattedDateFrom, formattedDateTo]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

   const enterFullscreen = () => {
      if (!matrixRef.current) return;
    
      if (matrixRef.current.requestFullscreen) {
        matrixRef.current.requestFullscreen();
      } else if (matrixRef.current.webkitRequestFullscreen) {
        matrixRef.current.webkitRequestFullscreen();
      }
    };
    
    const exitFullscreen = () => {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    };
    
     
    useEffect(() => {
      const onChange = () => {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
        setIsFullscreen(Boolean(fsEl));
      };

      document.addEventListener("fullscreenchange", onChange);
      document.addEventListener("webkitfullscreenchange", onChange);

      return () => {
        document.removeEventListener("fullscreenchange", onChange);
        document.removeEventListener("webkitfullscreenchange", onChange);
      };
    }, []);

  // ✅ Build Return dropdown options dynamically
  const returnOptions = useMemo(() => {
    const set = new Set();
    companies.forEach((c) => {
      if (c?.Returnx) set.add(c.Returnx);
    });
    return ["ALL", ...Array.from(set).sort()];
  }, [companies]);

  // ✅ Apply ALL filters here (status + return + search)
  const filteredCompanies = useMemo(() => {
    const q = searchCompany.trim().toLowerCase();

    return companies.filter((c) => {
      const statusOk =
        statusFilter === STATUS_FILTERS.ALL ? true : c.Statusx === statusFilter;

      const returnOk = returnFilter === "ALL" ? true : c.Returnx === returnFilter;

      const name = (c.CompanyName || "").toLowerCase();
      const searchOk = q ? name.includes(q) : true;

      return statusOk && returnOk && searchOk;
    });
  }, [companies, statusFilter, returnFilter, searchCompany]);

  // ✅ Counts should match the filtered dataset for the date range,
  // but NOT depend on the statusFilter (otherwise Done/ToDo boxes shrink when you click them).
  // So compute counts from "companies filtered by return + search only" (optional best UX).
  const companiesForCounts = useMemo(() => {
    const q = searchCompany.trim().toLowerCase();
    return companies.filter((c) => {
      const returnOk = returnFilter === "ALL" ? true : c.Returnx === returnFilter;
      const name = (c.CompanyName || "").toLowerCase();
      const searchOk = q ? name.includes(q) : true;
      return returnOk && searchOk;
    });
  }, [companies, returnFilter, searchCompany]);

  const taskDone = useMemo(
    () => companiesForCounts.filter((c) => c.Statusx === "Done").length,
    [companiesForCounts]
  );
  const taskToDo = useMemo(
    () => companiesForCounts.filter((c) => c.Statusx === "To Do").length,
    [companiesForCounts]
  );
  const taskPending = useMemo(
    () => companiesForCounts.filter((c) => c.Statusx === "Pending").length,
    [companiesForCounts]
  );

  const taskDrafted = useMemo(
    () => companiesForCounts.filter((c) => c.Statusx === "Drafted").length,
    [companiesForCounts]
  );

  const taskForPayment = useMemo(
    () => companiesForCounts.filter((c) => c.Statusx === "For Payment").length,
    [companiesForCounts]
  );

  const totalTasks = useMemo(() => companiesForCounts.length, [companiesForCounts]);

  const clearFilters = () => {
    setStatusFilter(STATUS_FILTERS.ALL);
    setReturnFilter("ALL");
    setSearchCompany("");
  };

  // ✅ When date range changes, reset filters (optional)
  useEffect(() => {
    clearFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedDateFrom, formattedDateTo]);

  return (
    <Box
      ref={matrixRef}
      sx={{
        ...(isFullscreen
          ? {
              position: "relative",
              width: "100%",
              height: "100%",
              backgroundColor: colors.primary[400],
              p: 2,
              overflow: "auto",
            }
          : {}),
      }}
    >
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center"  style={styles.tableContainer} backgroundColor={colors.primary[400]}>
      {/* Date range */}
      <form style={styles.form}>
        <div style={styles.formGroup}>
          <SafeDatePicker
            label="Date From"
            value={selectedDateFrom}
            onChange={(d) => {
              setSelectedDateFrom(d);
              if (selectedDateTo && selectedDateTo < d) setSelectedDateTo(d);
            }}
          />
        </div>

        <div style={styles.formGroup}>
          <SafeDatePicker
            label="Date To"
            value={selectedDateTo}
            minDate={selectedDateFrom}
            onChange={setSelectedDateTo}
          />
        </div>

        {/* ✅ Return filter */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Return</label>
          <select
            value={returnFilter}
            onChange={(e) => setReturnFilter(e.target.value)}
            style={styles.select}
          >
            {returnOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt === "ALL" ? "All Returns" : opt}
              </option>
            ))}
          </select>
        </div>

        {/* ✅ Company search */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Company</label>
          <input
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
            placeholder="Search company..."
            style={styles.search}
          />
        </div>
        {/* ✅ Clear filter button */}
        <button type="button" onClick={clearFilters} style={styles.clearBtn}>
          Clear filters
        </button>

        </form>

          <Box>
          <IconButton
          onClick={() => (isFullscreen ? exitFullscreen() : enterFullscreen())}
          sx={{
            backgroundColor: colors.grey[700],
            color: colors.grey[100],
            borderRadius: 1,
            px: 1.5,
            py: 1,
            "&:hover": { backgroundColor: colors.grey[600] },
          }}
        >
        <FullscreenIcon />
          <Typography sx={{ ml: 1, fontSize: 12 }}>
            {isFullscreen ? "Exit Full Screen" : "Full Screen"}
          </Typography>
        </IconButton>

        </Box>
        
    
        </Box>
      {/* Counts + Clear filter */}
      <div style={styles.countContainer}>
      <div style={styles.countRow}>
        <div style={styles.countContainer}>
          <ToDoCount
            count={taskDone}
            label="Done"
            backgroundColor="#28a745"
            active={statusFilter === STATUS_FILTERS.DONE}
            onClick={() => setStatusFilter(STATUS_FILTERS.DONE)}
          />
           {taskToDo > 0 && ( <ToDoCount
            count={taskToDo}
            label="To Do"
            backgroundColor="#FF9EAA"
            active={statusFilter === STATUS_FILTERS.TODO}
            onClick={() => setStatusFilter(STATUS_FILTERS.TODO)}
            />)}
          
          {taskDrafted > 0 && ( <ToDoCount
            count={taskDrafted}
            label="Drafted"
            backgroundColor="#FF9EAA"
            active={statusFilter === STATUS_FILTERS.DRAFTED}
            onClick={() => setStatusFilter(STATUS_FILTERS.DRAFTED)}
            />)}
            {taskPending > 0 && ( <ToDoCount
            count={taskPending}
            label="Pending"
            backgroundColor="#FF9EAA"
            active={statusFilter === STATUS_FILTERS.PENDING}
            onClick={() => setStatusFilter(STATUS_FILTERS.PENDING)}
            />)}

            {taskForPayment > 0 && ( <ToDoCount
            count={taskForPayment}
            label="For Payment"
            backgroundColor="#FF9EAA"
            active={statusFilter === STATUS_FILTERS.FOR_PAYMENT}
            onClick={() => setStatusFilter(STATUS_FILTERS.FOR_PAYMENT)}
            />)}
          <ToDoCount
            count={totalTasks}
            label="Total"
            active={statusFilter === STATUS_FILTERS.ALL}
            onClick={() => setStatusFilter(STATUS_FILTERS.ALL)}
          />
        </div>

     
      </div>
        </div>
      {/* ✅ Charts now follow the filter */}
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}> 
        {/* <div style={styles.countContainer}> */}
        {/* <div style={styles.dashboardGraphs}> */}
        <BarchartReturns companies={filteredCompanies} />
        <BarchartStatus companies={filteredCompanies} />
             {/* </div> */}
         {/* </div> */}
    </Stack>

      {/* ✅ Company list follows the filter */}
      <CompanyList
        companies={filteredCompanies}
        dateFrom={formattedDateFrom}
        dateTo={formattedDateTo}
        refreshCompanies={fetchCompanies}
      />
    </div>
    </Box>
  );
};

const styles = {
  form: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "flex-start",
    gap: "16px",
    marginBottom: "10px",
    flexWrap: "wrap",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
  },
  label: { fontSize: 12, opacity: 0.8 },

  select: {
    height: 36,
    padding: "0 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    minWidth: 180,
  },
  search: {
    height: 36,
    padding: "0 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    minWidth: 220,
  },

  countRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  countContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  clearBtn: {
    height: 36,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid #ccc",
    background: "#fff",
    cursor: "pointer",
  },
  dashboardGraphs: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    flexWrap: "wrap",
    gap: "16px",
  },
};

export default ClientMonitoring;
